import toast from 'react-hot-toast';
import { handleInsufficientTokensError } from './insufficientTokensHandler';

/**
 * Error filter configuration
 */
interface ErrorFilter {
  type?: string; // Error type/class name (e.g., 'NetworkError', 'TypeError')
  messagePattern?: RegExp | string; // Error message pattern (regex or string)
  errorCode?: string; // Error code property value
  predicate?: (error: any) => boolean; // Custom predicate function
}

/**
 * Registry of ignored error patterns
 */
const ignoredErrors: ErrorFilter[] = [];

/**
 * Track recent errors to prevent duplicate notifications
 */
const recentErrors = new Map<string, number>();
const DUPLICATE_WINDOW_MS = 5000; // 5 seconds

/**
 * Check if an error matches any ignore pattern
 */
export function shouldIgnoreError(error: any): boolean {
  for (const filter of ignoredErrors) {
    // Check by error type
    if (filter.type) {
      const errorType = error?.constructor?.name || error?.name || '';
      if (errorType === filter.type) {
        return true;
      }
    }

    // Check by error message pattern
    if (filter.messagePattern) {
      const errorMessage = error?.message || String(error) || '';
      if (typeof filter.messagePattern === 'string') {
        if (errorMessage.includes(filter.messagePattern)) {
          return true;
        }
      } else if (filter.messagePattern instanceof RegExp) {
        if (filter.messagePattern.test(errorMessage)) {
          return true;
        }
      }
    }

    // Check by error code
    if (filter.errorCode !== undefined) {
      const errorCode = error?.errorCode || error?.code || error?.statusCode;
      if (errorCode === filter.errorCode) {
        return true;
      }
    }

    // Check by custom predicate
    if (filter.predicate) {
      try {
        if (filter.predicate(error)) {
          return true;
        }
      } catch (predicateError) {
        // Silently handle predicate errors
      }
    }
  }

  return false;
}

/**
 * Check if this is a duplicate error (same error shown recently)
 */
function isDuplicateError(errorMessage: string): boolean {
  const now = Date.now();
  const lastShown = recentErrors.get(errorMessage);

  if (lastShown && now - lastShown < DUPLICATE_WINDOW_MS) {
    return true;
  }

  recentErrors.set(errorMessage, now);
  
  // Clean up old entries
  for (const [message, timestamp] of recentErrors.entries()) {
    if (now - timestamp >= DUPLICATE_WINDOW_MS) {
      recentErrors.delete(message);
    }
  }

  return false;
}

/**
 * Extract user-friendly error message from various error types
 */
function extractErrorMessage(error: any): string {
  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message.split('\n')[0];
    return message.length > 150 ? `${message.substring(0, 150)}...` : message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error.length > 150 ? `${error.substring(0, 150)}...` : error;
  }

  // Handle objects with message property
  if (error?.message) {
    const message = String(error.message);
    return message.length > 150 ? `${message.substring(0, 150)}...` : message;
  }

  // Handle network/API errors
  if (error?.status) {
    if (error.status === 404) {
      return 'Resource not found';
    }
    if (error.status === 403) {
      return 'Access denied';
    }
    if (error.status === 401) {
      return 'Authentication required';
    }
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    return `Request failed (${error.status})`;
  }

  // Handle error with detail property
  if (error?.detail) {
    return String(error.detail);
  }

  // Generic fallback
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Handle and display an error
 */
function handleError(error: any, source?: string) {
  // Check if error should be ignored
  if (shouldIgnoreError(error)) {
    return;
  }

  // Handle 402 Insufficient Tokens error centrally
  if (handleInsufficientTokensError(error)) {
    // Error was handled by centralized handler - don't show generic error
    // Log full error details only when logging is enabled
    import('./logger').then(({ logger }) => {
      if (logger.isEnabled()) {
        logger.error(`Global error handler [${source || 'unknown'}]: Insufficient tokens`, error);
      }
    }).catch(() => {
      // Silently fail if logger is not available
    });
    return;
  }

  // Extract user-friendly message
  const errorMessage = extractErrorMessage(error);

  // Check for duplicates
  if (isDuplicateError(errorMessage)) {
    return;
  }

  // Show toast notification
  toast.error(errorMessage, {
    duration: 5000,
    position: 'top-right',
    style: {
      background: '#dc2626',
      color: '#fff',
      fontWeight: '500',
    },
  });

  // Log full error details only when logging is enabled
  // Import logger dynamically to avoid circular dependency
  import('./logger').then(({ logger }) => {
    if (logger.isEnabled()) {
      logger.error(`Global error handler [${source || 'unknown'}]:`, error);
    }
  }).catch(() => {
    // Silently fail if logger is not available
  });
}

/**
 * Register an error pattern to ignore
 */
export function ignoreError(filter: ErrorFilter): void {
  ignoredErrors.push(filter);
}

/**
 * Remove an ignored error pattern
 */
export function removeIgnoredError(filter: ErrorFilter): void {
  const index = ignoredErrors.findIndex((existingFilter) => {
    if (filter.type && existingFilter.type !== filter.type) return false;
    if (filter.messagePattern && existingFilter.messagePattern !== filter.messagePattern) return false;
    if (filter.errorCode !== undefined && existingFilter.errorCode !== filter.errorCode) return false;
    if (filter.predicate && existingFilter.predicate !== filter.predicate) return false;
    return true;
  });

  if (index !== -1) {
    ignoredErrors.splice(index, 1);
  }
}

/**
 * Clear all ignored error patterns
 */
export function clearIgnoredErrors(): void {
  ignoredErrors.length = 0;
}

/**
 * Get all currently ignored error patterns
 */
export function getIgnoredErrors(): ReadonlyArray<ErrorFilter> {
  return [...ignoredErrors];
}

/**
 * Initialize global error handlers
 */
export function initializeGlobalErrorHandlers(): void {
  // Handle JavaScript runtime errors
  window.onerror = (message, source, lineno, _colno, error) => {
    handleError(error || message, `runtime:${source}:${lineno}`);
    return false; // Don't prevent default error handling
  };

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleError(event.reason, 'unhandledrejection');
    // Don't prevent default handling (browser console will still show it)
  });

  // Handle resource loading errors (images, scripts, etc.)
  window.addEventListener('error', (event) => {
    // Only handle errors that aren't already caught by window.onerror
    if (event.error) {
      handleError(event.error, `resource:${event.target}`);
    }
  }, true); // Use capture phase
}

